<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleAgendaCaseHeaderChips from './PostSaleAgendaCaseHeaderChips.vue';
import { presentAgendaAddress } from '../composables/postSaleAgendaPresenter';

const props = defineProps({
  postSaleCase: { type: Object, required: true },
});

const emit = defineEmits(['action', 'open']);
const { t } = useI18n();
const addressLabel = computed(
  () =>
    presentAgendaAddress(props.postSaleCase.transaction?.address) ||
    t('POST_SALE.COMMON.ADDRESS_UNAVAILABLE')
);

const bucketClass = bucket =>
  ({
    amarelos: 'border-[#FFC43E] bg-[#FFFAF0] text-[#5D696F]',
    laranjas: 'border-[#FF7A11] bg-[#FFF1E5] text-[#5D696F]',
    vermelhos: 'border-[#E33838] bg-[#FDF2F2] text-[#5D696F]',
  })[bucket] || 'border-[#E0E7EB] bg-[#F6F8F9] text-[#5D696F]';
const deadlineLabel = bucket =>
  ({
    amarelos: t('POST_SALE.DEADLINES.AMARELOS'),
    laranjas: t('POST_SALE.DEADLINES.LARANJAS'),
    vermelhos: t('POST_SALE.DEADLINES.VERMELHOS'),
  })[bucket];

const itbiMessage = (key, params) =>
  ({
    'POST_SALE.DEADLINES.ITBI_PROTOCOL_EXPIRED': () =>
      t('POST_SALE.DEADLINES.ITBI_PROTOCOL_EXPIRED', params),
    'POST_SALE.DEADLINES.ITBI_PROTOCOL_EXPIRES_TODAY': () =>
      t('POST_SALE.DEADLINES.ITBI_PROTOCOL_EXPIRES_TODAY'),
    'POST_SALE.DEADLINES.ITBI_GUIDE_DUE': () =>
      t('POST_SALE.DEADLINES.ITBI_GUIDE_DUE', params),
    'POST_SALE.DEADLINES.ITBI_GUIDE_AVAILABLE': () =>
      t('POST_SALE.DEADLINES.ITBI_GUIDE_AVAILABLE'),
    'POST_SALE.DEADLINES.ITBI_PENDING_OVERDUE': () =>
      t('POST_SALE.DEADLINES.ITBI_PENDING_OVERDUE', params),
    'POST_SALE.DEADLINES.ITBI_PENDING_OVERDUE_TODAY': () =>
      t('POST_SALE.DEADLINES.ITBI_PENDING_OVERDUE_TODAY'),
    'POST_SALE.DEADLINES.ITBI_PENDING_DUE': () =>
      t('POST_SALE.DEADLINES.ITBI_PENDING_DUE', params),
    'POST_SALE.DEADLINES.ITBI_PENDING_ATTENTION': () =>
      t('POST_SALE.DEADLINES.ITBI_PENDING_ATTENTION'),
  })[key]?.();

const deadlineMessage = deadline => {
  if (deadline.message_key) {
    return itbiMessage(deadline.message_key, deadline.message || {});
  }
  if (deadline.bucket === 'vermelhos') {
    return t('POST_SALE.DEADLINES.MESSAGE_OVERDUE', deadline.message);
  }
  if (deadline.bucket === 'laranjas') {
    return t('POST_SALE.DEADLINES.MESSAGE_DUE_SOON', deadline.message);
  }
  return t('POST_SALE.DEADLINES.MESSAGE_ATTENTION', deadline.message);
};
const actionLabels = {
  request_service: t('POST_SALE.AGENDA.ACTIONS.REQUEST_SERVICE'),
  schedule_deed: t('POST_SALE.AGENDA.ACTIONS.SCHEDULE_DEED'),
  generate_itbi_document: t('POST_SALE.AGENDA.ACTIONS.GENERATE_ITBI_DOCUMENT'),
};
const actionLabelKeys = {
  'POST_SALE.AGENDA.ACTIONS.REQUEST_SERVICE': t(
    'POST_SALE.AGENDA.ACTIONS.REQUEST_SERVICE'
  ),
  'POST_SALE.AGENDA.ACTIONS.SCHEDULE_DEED': t(
    'POST_SALE.AGENDA.ACTIONS.SCHEDULE_DEED'
  ),
  'POST_SALE.AGENDA.ACTIONS.GENERATE_ITBI_NEW_DOCUMENT': t(
    'POST_SALE.AGENDA.ACTIONS.GENERATE_ITBI_NEW_DOCUMENT'
  ),
};
const deadlineActionLabel = deadline =>
  deadline.action?.label_key
    ? actionLabelKeys[deadline.action.label_key] ||
      actionLabels[deadline.action?.code]
    : actionLabels[deadline.action?.code] ||
      deadline.action?.label ||
      `${deadlineLabel(deadline.bucket)} ${t('POST_SALE.COMMON.SEPARATOR')} ${t(
        'POST_SALE.ACTIONS.DETAILS'
      )}`;
</script>

<template>
  <article
    data-testid="post-sale-agenda-case"
    class="border-l-[3px] border-l-transparent px-[29px] py-7 hover:border-l-lais-primary-200"
  >
    <div>
      <button
        type="button"
        class="text-left font-semibold text-lais-text hover:text-lais-primary"
        @click="emit('open', postSaleCase)"
      >
        {{ addressLabel }}
      </button>
      <PostSaleAgendaCaseHeaderChips
        :deadlines="postSaleCase.deadlines"
        :responsible="postSaleCase.responsible"
      />
    </div>
    <div class="mt-4 grid gap-3">
      <div
        v-for="(deadline, index) in postSaleCase.deadlines"
        :key="`${deadline.bucket}-${index}`"
        class="flex flex-col gap-3 rounded-lais-sm border p-3 sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
        :class="bucketClass(deadline.bucket)"
      >
        <div class="flex items-start gap-2 text-sm">
          <span
            class="i-lucide-circle-alert mt-0.5 size-4 shrink-0"
            aria-hidden="true"
          />
          <p>{{ deadlineMessage(deadline) }}</p>
        </div>
        <LaisPrimaryButton
          size="sm"
          class="!bg-[#01988C] !text-white hover:!bg-[#01655D]"
          @click="
            deadline.action
              ? emit('action', { postSaleCase, action: deadline.action })
              : emit('open', postSaleCase)
          "
        >
          {{ deadlineActionLabel(deadline) }}
        </LaisPrimaryButton>
      </div>
    </div>
  </article>
</template>
